import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SearchOutlined } from "@ant-design/icons";
import { Spin, message } from "antd";
import styles from "./CreateYourFirstJob.module.css";
import SignupLayout from "../SignupLayout/SignupLayout";
import { CustomSelect } from "../components/CustomSelect/CustomSelect";
import { updateCompanyProfileDetails, getPersonalDetailsLocation, writeWithAi, createFirstJob, SearchValue } from "../../../redux/actions";
import { searchProfessionsApi } from "../../../API/API";
import { getSelectedPlan, markUpgradeModalForSignupSuccess } from "../../../shared/billing";
import { setCookie } from "../../../shared/helpers";

// Emojis / pictographs (incl. flags, keycaps, ZWJ sequences & variation selectors)
const EMOJI_REGEX = new RegExp(
  "(?:" +
    "[\\u2190-\\u21FF\\u2300-\\u23FF\\u2460-\\u24FF\\u25A0-\\u27BF" +
    "\\u2900-\\u297F\\u2B00-\\u2BFF\\u3030\\u303D\\u3297\\u3299]" +
    "|[\\uD83C-\\uD83E][\\uDC00-\\uDFFF]" +
    ")[\\uFE00-\\uFE0F\\u200D\\u20E3]*",
  "g"
);

const stripEmojis = (text = "") => text.replace(EMOJI_REGEX, "");

// Strip emojis and clean up the whitespace they leave behind (used before submitting)
const cleanNlpText = (text = "") =>
  stripEmojis(text)
    .replace(/^[ \t]+|[ \t]+$/gm, "")
    .trim();

const WORK_TYPE_OPTIONS = [
  { label: "Onsite", value: "Onsite" },
  { label: "Remote", value: "Remote" },
  { label: "Hybrid", value: "Hybrid" },
    // { label: "Contract", value: "Contract" },
];

export default function CreateYourFirstJob() {
  const history = useHistory();
  const { state: routeState } = useLocation();
  const dispatch = useDispatch();

  const aiLoading = useSelector((state) => state.writeWithAiReducer?.loading);
  const profile = useSelector((state) => state?.profileReducer?.user?.profile);
  const [professionSuggestions, setProfessionSuggestions] = useState([]);
  const [professionCode, setProfessionCode] = useState("");
  const [professionLoading, setProfessionLoading] = useState(false);
  const locationSuggestions = useSelector((state) => state.getPersonalDetailsLocationReducer?.location);

  const [roleTitle, setRoleTitle] = useState("");
  const [hiringCompany, setHiringCompany] = useState(
    routeState?.companyName || profile?.CompanyName || ""
  );
  const [workType, setWorkType] = useState("Onsite");
  const [location, setLocation] = useState("");
  const [locationCity, setLocationCity] = useState("");
  const [locationState, setLocationState] = useState("");
  const [locationCountry, setLocationCountry] = useState("");
  const [nlpText, setNlpText] = useState("");
  const [nlpError, setNlpError] = useState("");
  const [aiTyping, setAiTyping] = useState(false);
  const typewriterRef = useRef(null);
  const [findTalentsLoading, setFindTalentsLoading] = useState(false);
  const aiGeneratedTextRef = useRef('');
  const [isWorkTypeOpen, setIsWorkTypeOpen] = useState(false);
  const [showRoleSuggestions, setShowRoleSuggestions] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  const roleTitleWrapRef = useRef(null);
  const locationWrapRef = useRef(null);
  const isSelectingLocationRef = useRef(false);
  const isSelectingRoleTitleRef = useRef(false);

  useEffect(() => {
    setHiringCompany((prev) => prev || profile?.CompanyName || "");
  }, [profile]);

  useEffect(() => {
    if (!roleTitle.trim()) { setProfessionSuggestions([]); setShowRoleSuggestions(false); setProfessionLoading(false); return; }
    if (isSelectingRoleTitleRef.current) { isSelectingRoleTitleRef.current = false; return; }
    setProfessionLoading(true);
    setShowRoleSuggestions(true);
    const timer = setTimeout(() => {
      searchProfessionsApi(roleTitle)
        .then((res) => {
          const list = res?.data?.data?.results || [];
          setProfessionSuggestions(Array.isArray(list) ? list : []);
          setProfessionLoading(false);
        })
        .catch(() => { setProfessionLoading(false); });
    }, 600);
    return () => clearTimeout(timer);
  }, [roleTitle]);

  useEffect(() => {
    if (!location.trim()) { setShowLocationSuggestions(false); return; }
    if (isSelectingLocationRef.current) { isSelectingLocationRef.current = false; return; }
    const timer = setTimeout(() => {
      dispatch(getPersonalDetailsLocation(location));
      setShowLocationSuggestions(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [location, dispatch]);

  useEffect(() => {
    if (!showRoleSuggestions) return;
    const handler = (e) => {
      if (roleTitleWrapRef.current && !roleTitleWrapRef.current.contains(e.target))
        setShowRoleSuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showRoleSuggestions]);

  useEffect(() => {
    if (!showLocationSuggestions) return;
    const handler = (e) => {
      if (locationWrapRef.current && !locationWrapRef.current.contains(e.target))
        setShowLocationSuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showLocationSuggestions]);

  const isDisabled = !roleTitle.trim() || !hiringCompany.trim() || !workType || (workType !== "Remote" && !location.trim()) || aiLoading || aiTyping || !cleanNlpText(nlpText);

  const handleWriteWithAi = () => {
    const prompt = nlpText.trim() ? nlpText : roleTitle;
    if (nlpText.trim() && nlpText.trim().length < 20) {
      setNlpError("Please write at least 20 characters.");
      return;
    }
    setNlpError("");
    dispatch(writeWithAi({
      title: roleTitle,
      companyName: hiringCompany,
      locationType: workType.toLowerCase() === "onsite" ? "on-site" : workType.toLowerCase(),
      city: locationCity,
      state: locationState,
      country: locationCountry,
      prompt: prompt,
      onSuccess: (data) => {
        if (data?.data?.talentFilters) {
          localStorage.setItem("aiTalentFilters", JSON.stringify(data.data.talentFilters));
        }
        if (data?.data?.generatedText) {
          const fullText = cleanNlpText(data.data.generatedText);
          aiGeneratedTextRef.current = fullText;
          setNlpText("");
          setAiTyping(true);
          if (typewriterRef.current) clearInterval(typewriterRef.current);
          let i = 0;
          typewriterRef.current = setInterval(() => {
            i++;
            setNlpText(fullText.slice(0, i));
            if (i >= fullText.length) {
              clearInterval(typewriterRef.current);
              setAiTyping(false);
            }
          }, 18);
        }
      },
    }));
  };

  const handleFindTalents = () => {
    if (isDisabled || findTalentsLoading) return;
    setFindTalentsLoading(true);
    const cleanedNlpText = cleanNlpText(nlpText);
    dispatch(createFirstJob({
      title: roleTitle,
      companyName: hiringCompany,
      location,
      locationType: workType.toLowerCase(),
      description: cleanedNlpText,
      prompt: cleanedNlpText,
      WizardFlow: 610,
      onSuccess: () => {
        // Navigate first (WizardFlow still 610), so SignupRoute doesn't intercept
        setCookie('finishProfile', 'true');
        dispatch(SearchValue(roleTitle));
        localStorage.setItem("nlpDescription", cleanedNlpText);
        if (aiGeneratedTextRef.current && cleanedNlpText !== cleanNlpText(aiGeneratedTextRef.current)) {
          localStorage.removeItem("aiTalentFilters");
        }
        if (workType === "Remote") localStorage.removeItem("nlpJobLocation");
        else if (location.trim()) localStorage.setItem("nlpJobLocation", location.trim());
        history.push(`/query/${encodeURIComponent(roleTitle)}`);
        // Update WizardFlow in background after navigation 
        dispatch(updateCompanyProfileDetails({
          WizardFlow: 1000,
          onSuccess: () => {
            if (getSelectedPlan()) markUpgradeModalForSignupSuccess();
          },
          onError: () => {},
        }));
      },
      onError: (err) => {
        const errMsg = err?.response?.data?.message || "Something went wrong. Please try again.";
        message.error(errMsg);
        setFindTalentsLoading(false);
      },
    }));
  };

  return (
    <SignupLayout
      step={2}
      totalSegments={3}
      wrapperMaxWidth="800px"
      nextFooterMaxWidth="800px"
      heading="Create Your First Job"
      subHeading="Skillgigs AI-powered sourcing will recommended a massive list of best fit talents for your job."
      onNext={handleFindTalents}
      isNextDisabled={isDisabled}
      nextText="Find Talents"
      nextLoading={findTalentsLoading}
      nextWidth="752px"
      showBack={false}
      scrollable
    >
      {/* Role Title */}
      <div className={styles.field}>
        <div className={styles.sectionLabel}>
          Role Title <span className={styles.required}>*</span>
        </div>
        <div className={styles.autocompleteWrap} ref={roleTitleWrapRef}>
          <input
            className={styles.control}
            value={roleTitle}
            placeholder="i.e. Senior Software Engineer"
            onChange={(e) => setRoleTitle(e.target.value)}
            onFocus={() => { if (roleTitle && professionSuggestions?.length > 0) setShowRoleSuggestions(true); }}
          />
          {showRoleSuggestions && (professionLoading || professionSuggestions?.length > 0) && (
            <div className={styles.suggestionsList}>
              {professionLoading ? (
                <div className={styles.suggestionLoader}>
                  <Spin size="small" />
                </div>
              ) : (
                professionSuggestions.map((item, i) => (
                  <div
                    key={i}
                    className={styles.suggestionItem}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      isSelectingRoleTitleRef.current = true;
                      setRoleTitle(item.title);
                      setProfessionCode(item.professionCode || "");
                      setShowRoleSuggestions(false);
                    }}
                  >
                    {item.title}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Hiring Company */}
      <div className={styles.field}>
        <div className={styles.sectionLabel}>
          Hiring Company <span className={styles.required}>*</span>
        </div>
        <input
          className={styles.control}
          value={hiringCompany}
          placeholder="Company Name"
          onChange={(e) => setHiringCompany(e.target.value)}
        />
      </div>

      {/* Location */}
      <div className={styles.sectionLabel}>
        Location <span className={styles.required}>*</span>
      </div>
      <div className={styles.locationRow}>
        <div className={styles.locationHalf}>
          <CustomSelect
            label=""
            placeholder="Onsite"
            value={workType}
            open={isWorkTypeOpen}
            setOpen={setIsWorkTypeOpen}
            onChange={setWorkType}
            options={WORK_TYPE_OPTIONS}
            showSearch={false}
          />
        </div>
        {workType !== "Remote" && (
          <div className={styles.locationHalf}>
            <div className={styles.autocompleteWrap} ref={locationWrapRef}>
              <input
                className={styles.locationInput}
                value={location}
                placeholder="City, state or country"
                onChange={(e) => setLocation(e.target.value)}
                onFocus={() => { if (location && locationSuggestions?.length > 0) setShowLocationSuggestions(true); }}
              />
              {showLocationSuggestions && locationSuggestions?.length > 0 && (
                <div className={styles.suggestionsList}>
                  {locationSuggestions.map((item, i) => (
                    <div
                      key={i}
                      className={styles.suggestionItem}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        isSelectingLocationRef.current = true;
                        setLocation(item.LocationWithCountry);
                        setLocationCity(item.CityName || "");
                        setLocationState(item.RegionName || "");
                        setLocationCountry(item.CountryName || "");
                        setShowLocationSuggestions(false);
                      }}
                    >
                      {item.LocationWithCountry}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Natural Language Search */}
      <div className={styles.nlpSection}>
        <div className={styles.nlpHeader}>
          <span className={styles.nlpIcon}>
            <SearchOutlined />
          </span>
          <span className={styles.nlpTitle}>Natural Language Search</span>
        </div>
        <p className={styles.nlpDesc}>
          Describe talent nice-to-haves in as much detail as possible so Skillgigs can source top matches.
          For any absolute must-haves, you can use filters or booleans on the next page.
        </p>
        <div className={styles.nlpBox}>
          {aiLoading ? (
            <div className={styles.nlpSkeleton}>
              <div className={styles.nlpSkeletonLine} />
              <div className={`${styles.nlpSkeletonLine} ${styles.nlpSkeletonLineMid}`} />
              <div className={`${styles.nlpSkeletonLine} ${styles.nlpSkeletonLineShort}`} />
            </div>
          ) : (
            <textarea
              className={`${styles.nlpTextarea} ${aiTyping ? styles.nlpTextareaTyping : nlpText ? styles.nlpTextareaDone : ''}`}
              value={nlpText}
              readOnly={aiTyping}
              onChange={(e) => { if (!aiTyping) { setNlpText(stripEmojis(e.target.value)); if (nlpError) setNlpError(""); } }}
              placeholder={
                "e.g. Experience in Fintech or SaaS startups\nHas mentored Junior Engineers and is an expert at refactoring code\nCommunicates well with C-suite executives"
              }
            />
          )}
          {!nlpText.trim() && !aiLoading && !aiTyping && (
            <button
              className={styles.writeAiBtn}
              type="button"
              onClick={handleWriteWithAi}
            >
              ✦ Write with AI
            </button>
          )}
          {nlpError && <p style={{ color: "red", fontSize: 12, margin: "4px 0 0" }}>{nlpError}</p>}
        </div>
      </div>
    </SignupLayout>
  );
}
